import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import JobsView from './sections/jobs/JobsView';

import apiService from '../../services/api';
import AdminSidebar from './components/AdminSidebar/AdminSidebar';

import SuperAdminView     from './sections/SuperAdminView/SuperAdminView';
import DashboardView      from './sections/DashboardView/DashboardView';
import ProductsView       from './sections/ProductsView/ProductsView';
import PromotionsView     from './sections/PromotionsView/PromotionsView';  
import UsersView          from './sections/UsersView/UsersView';
import ShopsView          from './sections/ShopsView/ShopsView';
import CategoriesView     from './sections/CategoriesView/CategoriesView';
import ReviewsView        from './sections/ReviewsView/ReviewsView';
import AnalyticsView      from './sections/AnalyticsView/AnalyticsView';
import UserCommunications from './sections/UserComms/UserCommunications';
import ChatBotView        from './sections/ChatBotView/ChatBotView';
import ReferredUsersPage from './sections/ReferredUsersPage/ReferredUsersPage';

const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';
const PRODUCTS_PER_PAGE = 100;

function AdminDashboard({ onLogout }) {
  const [stats, setStats]               = useState(null);
  const [revenueData, setRevenueData]   = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [products, setProducts]         = useState([]);
  const [orders, setOrders]             = useState([]);   // ✅ kept for any future order views
  const [users, setUsers]               = useState([]);
  const [shops, setShops]               = useState([]);
  const [categories, setCategories]     = useState([]);
  const [activePeriod, setActivePeriod] = useState('month');
  const [loading, setLoading]           = useState(true);
  const [referrals, setReferrals]             = useState([]);
  const [referralStats, setReferralStats]     = useState(null);

  // ✅ Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [productsPagination, setProductsPagination] = useState({
    currentPage: 1, totalPages: 1, totalCount: 0, hasNext: false, hasPrevious: false,
  });
  const [productStats, setProductStats] = useState(null);
  const [productsFilters, setProductsFilters] = useState({
    category: '', status: '', search: '', plan_type: '',
  });

  const [currentView, setCurrentView] = useState(() => {
    const saved = localStorage.getItem('adminCurrentView') || 'overview';
    // ✅ Migrate any saved 'orders' to 'promotions' automatically
    return saved === 'orders' ? 'promotions' : saved;
  });

  useEffect(() => {
    if (currentView) localStorage.setItem('adminCurrentView', currentView);
  }, [currentView]);


  // ── Admin guard — runs on mount + focus + tab visibility ─────────────────────
  // Checks /auth/profile/ and kicks the user out immediately if:
  //   1. No token present
  //   2. Profile returns 401/403 (suspended, token invalid, not in UserProfile)
  //   3. Profile returns 404 (email not found in UserProfile table)
  //   4. is_staff === false (logged in but not an admin)
  //   5. is_active === false (account suspended)
  // Uses email from the profile response to confirm the user exists in UserProfile.
  useEffect(() => {
    // Track mount time to avoid aggressive logout on OAuth race
    if (!window._adminDashboardMountTime) {
      window._adminDashboardMountTime = Date.now();
    }
    const guardAdmin = async () => {
      const token = (localStorage.getItem('authToken') || localStorage.getItem('token')) || localStorage.getItem('authToken') || '';
      if (!token) {
        console.warn('AdminDashboard guardAdmin: no token on this check (may be OAuth race) - skipping check.');
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/auth/profile/`, {
          headers: { Authorization: `Token ${token}` },
        });

        // 401 / 403 → token invalid, suspended, or not in UserProfile
        if (res.status === 401 || res.status === 403) {
          console.warn('AdminDashboard guardAdmin: profile returned ' + res.status + ' - token may be invalid.');
          const timeSinceMount = Date.now() - window._adminDashboardMountTime;
          if (timeSinceMount > 5000) {
            console.warn('Session expired, forcing logout.');
            onLogout?.();
          }
          return;
        }

        // 404 → email not found in UserProfile table
        if (res.status === 404) {
          console.warn('AdminDashboard guardAdmin: user not found in UserProfile.');
          const timeSinceMount = Date.now() - window._adminDashboardMountTime;
          if (timeSinceMount > 5000) {
            console.warn('User not found, forcing logout.');
            onLogout?.();
          }
          return;
        }

        if (!res.ok) {
          // Any other non-OK status — play it safe
          console.warn(`🔒 AdminDashboard: unexpected profile status ${res.status} — forcing logout.`);
          onLogout?.();
          return;
        }

        const profile = await res.json();

        // Not an admin
        if (!profile.is_staff && !profile.is_superuser) {
          console.warn('🔒 AdminDashboard: user is not staff/superuser — redirecting to home.');
          onLogout?.();
          return;
        }

        // Suspended
        if (profile.is_active === false) {
          console.warn('🔒 AdminDashboard: account suspended (is_active=false) — forcing logout.');
          onLogout?.();
          return;
        }

        // ✅ All checks passed — user is a valid, active admin
      } catch (err) {
        // Network error — don't kick the user out for a flaky connection
        console.warn('AdminDashboard guard: network error (non-fatal):', err.message);
      }
    };

    // 1. Run on mount - delayed to allow token to persist after OAuth login
    setTimeout(guardAdmin, 4000); // 4s delay for OAuth token to settle

    // 2. Run when user returns from another app/window
    window.addEventListener('focus', guardAdmin);

    // 3. Run when user switches back to this tab
    const onVisibility = () => {
      if (document.visibilityState === 'visible') guardAdmin();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', guardAdmin);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [onLogout]);

  useEffect(() => { setTimeout(fetchDashboardData, 3500); }, []);

  useEffect(() => {
    if (currentView === 'overview' || currentView === 'admin') fetchRevenueData(activePeriod);
  }, [activePeriod, currentView]);

  useEffect(() => {
    if (currentView === 'products') fetchProducts(productsPagination.currentPage);
  }, [productsPagination.currentPage, productsFilters]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
      if (!token) { console.warn('fetchDashboardData: no token yet, skipping.'); return; }

      const statsRes = await fetch(`${API_BASE_URL}/admin/dashboard/stats/`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      } else if (statsRes.status === 401 || statsRes.status === 403) {
        console.warn('fetchDashboardData: 401/403 on stats, skipping.'); return;
      }

      const actRes = await fetch(`${API_BASE_URL}/admin/dashboard/activity/`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (actRes.ok) setRecentActivity(await actRes.json());

      await fetchProducts(1);
      setOrders(generateMockOrders());
      setUsers(generateMockUsers());
      setShops(generateMockShops());
      setCategories(generateMockCategories());
      setLoading(false);
    } catch (e) {
      console.error('❌ Dashboard error:', e);
      setLoading(false);
      alert('Failed to load dashboard data.');
    }
  };

  const fetchProducts = async (page = 1) => {
    try {
      const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
      const params = new URLSearchParams({ page, page_size: PRODUCTS_PER_PAGE });
      if (productsFilters.category)  params.append('category',  productsFilters.category);
      if (productsFilters.status)    params.append('status',    productsFilters.status);
      if (productsFilters.search)    params.append('search',    productsFilters.search);
      if (productsFilters.plan_type) params.append('plan_type', productsFilters.plan_type);

      const res = await fetch(`${API_BASE_URL}/admin/products/?${params}`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;

      const data        = await res.json();
      const rawProducts = data.results || (Array.isArray(data) ? data : []);
      const serverTotal = data.count  || rawProducts.length;
      const transformed = rawProducts.map(p => apiService.transformAdminProductData(p));

      setProducts(transformed);
      setProductsPagination({
        currentPage: page, totalPages: Math.ceil(serverTotal / PRODUCTS_PER_PAGE),
        totalCount: serverTotal, hasNext: !!data.next, hasPrevious: !!data.previous,
      });

      const ago7 = new Date(); ago7.setDate(ago7.getDate() - 7);
      setProductStats({
        total:    serverTotal,
        active:   transformed.filter(p => p.status === 'Active' || p.is_active).length,
        inactive: transformed.filter(p => p.status !== 'Active' && !p.is_active).length,
        newLast7: transformed.filter(p => { const d = new Date(p.date_added || p.created_at); return !isNaN(d) && d >= ago7; }).length,
      });
        if (rawProducts.length > 0 && page === 1) {
          const sample = rawProducts[0];
        }
    } catch (e) { console.error('❌ fetchProducts:', e); }
  };

  const fetchRevenueData = async (period) => {
    try {
      const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
      const res   = await fetch(`${API_BASE_URL}/admin/dashboard/revenue/?period=${period}`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) { const d = await res.json(); setRevenueData(d.data || []); }
    } catch (e) { console.error('Revenue error:', e); }
  };

  const fetchReferralData = async () => {
    try {
      const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
      const res = await fetch(`${API_BASE_URL}/admin/referrals/`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setReferrals(data.results || data.referrals || []);
        setReferralStats(data.stats || { total: data.length, active: 0, earnings: 0 });
      }
    } catch (e) {
      console.error('❌ Fetch referrals error:', e);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
      const res   = await fetch(`${API_BASE_URL}/admin/products/${productId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) fetchProducts(productsPagination.currentPage);
    } catch (e) { console.error('Delete error:', e); }
  };

  const handleNextPage     = () => { if (productsPagination.hasNext)     setProductsPagination(p => ({ ...p, currentPage: p.currentPage + 1 })); };
  const handlePreviousPage = () => { if (productsPagination.hasPrevious) setProductsPagination(p => ({ ...p, currentPage: p.currentPage - 1 })); };
  const handleGoToPage     = (page) => { if (page >= 1 && page <= productsPagination.totalPages) setProductsPagination(p => ({ ...p, currentPage: page })); };

  const generateMockOrders = () => [
    { id: 1, orderId: '#ORD-2024-001', customer: 'John Doe',       product: 'iPhone 13 Pro',        amount: 125000, status: 'Completed',  date: '2024-02-10' },
    { id: 2, orderId: '#ORD-2024-002', customer: 'Jane Smith',     product: 'MacBook Air M2',       amount: 145000, status: 'Processing', date: '2024-02-11' },
    { id: 3, orderId: '#ORD-2024-003', customer: 'Mike Johnson',   product: 'AirPods Pro',          amount:  35000, status: 'Pending',    date: '2024-02-12' },
    { id: 4, orderId: '#ORD-2024-004', customer: 'Sarah Williams', product: 'iPad Pro 12.9"',       amount:  98000, status: 'Completed',  date: '2024-02-12' },
    { id: 5, orderId: '#ORD-2024-005', customer: 'David Brown',    product: 'Apple Watch Series 9', amount:  52000, status: 'Cancelled',  date: '2024-02-13' },
  ];

  const generateMockUsers = () => [
    { id: 1, name: 'Alice Johnson',  email: 'alice@example.com',  phone: '+254 712 345 678', status: 'Active',    joined: '2023-08-15', listings: 12 },
    { id: 2, name: 'Bob Wilson',     email: 'bob@example.com',    phone: '+254 723 456 789', status: 'Active',    joined: '2023-09-22', listings:  8 },
    { id: 3, name: 'Carol Martinez', email: 'carol@example.com',  phone: '+254 734 567 890', status: 'Suspended', joined: '2023-10-10', listings:  3 },
    { id: 4, name: 'Daniel Lee',     email: 'daniel@example.com', phone: '+254 745 678 901', status: 'Active',    joined: '2023-11-05', listings: 15 },
    { id: 5, name: 'Emma Davis',     email: 'emma@example.com',   phone: '+254 756 789 012', status: 'Active',    joined: '2024-01-12', listings:  6 },
  ];
  const generateMockShops = () => [
    { id: 1, name: 'Tech Haven',      owner: 'John Doe',       category: 'Electronics',   status: 'Approved', products: 45, rating: 4.8, joined: '2023-06-15' },
    { id: 2, name: 'Fashion Forward', owner: 'Jane Smith',     category: 'Fashion',       status: 'Pending',  products: 32, rating: 4.5, joined: '2024-01-20' },
    { id: 3, name: 'Home Essentials', owner: 'Mike Johnson',   category: 'Home & Garden', status: 'Approved', products: 67, rating: 4.9, joined: '2023-09-10' },
    { id: 4, name: 'Auto Parts Pro',  owner: 'Sarah Williams', category: 'Vehicles',      status: 'Approved', products: 28, rating: 4.6, joined: '2023-11-05' },
    { id: 5, name: 'Book Corner',     owner: 'David Brown',    category: 'Books',         status: 'Rejected', products:  0, rating:   0, joined: '2024-02-01' },
  ];
  const generateMockCategories = () => [
    { id: 1, name: 'Electronics',   icon: '📱', productCount: 245, status: 'Active', subCategories: 12 },
    { id: 2, name: 'Vehicles',      icon: '🚗', productCount: 189, status: 'Active', subCategories:  8 },
    { id: 3, name: 'Property',      icon: '🏠', productCount: 156, status: 'Active', subCategories:  6 },
    { id: 4, name: 'Fashion',       icon: '👗', productCount: 312, status: 'Active', subCategories: 15 },
    { id: 5, name: 'Home & Garden', icon: '🛋️', productCount: 203, status: 'Active', subCategories: 10 },
    { id: 6, name: 'Sports',        icon: '⚽', productCount:  98, status: 'Active', subCategories:  7 },
  ];

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading"><div className="spinner"></div><p>Loading dashboard...</p></div>
      </div>
    );
  }

  const renderView = () => {
    switch (currentView) {
      case 'admin':    return <SuperAdminView stats={stats} revenueData={revenueData} recentActivity={recentActivity} activePeriod={activePeriod} setActivePeriod={setActivePeriod} onLogout={onLogout} />;
      case 'overview': return <DashboardView stats={stats} revenueData={revenueData} recentActivity={recentActivity} products={products} activePeriod={activePeriod} setActivePeriod={setActivePeriod} setCurrentView={setCurrentView} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} />;
      case 'products': return <ProductsView products={products} productStats={productStats} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} pagination={productsPagination} onNextPage={handleNextPage} onPreviousPage={handlePreviousPage} onGoToPage={handleGoToPage} filters={productsFilters} onFilterChange={setProductsFilters} />;

      case 'promotions':     return <PromotionsView onLogout={onLogout} />;   // ✅ was 'orders'

      case 'users':          return <UsersView users={users} onLogout={onLogout} />;
      case 'referrals':      return <ReferredUsersPage referrals={referrals} stats={referralStats} onLogout={onLogout} />;
      case 'shops':          return <ShopsView shops={shops} onLogout={onLogout} />;
      case 'categories':     return <CategoriesView categories={categories} onLogout={onLogout} />;
      case 'jobs':           return <JobsView onLogout={onLogout} />;
      case 'reviews':        return <ReviewsView onLogout={onLogout} />;
      case 'analytics':      return <AnalyticsView onLogout={onLogout} />;
      case 'communications': return <UserCommunications />;
      case 'chatbot':        return <ChatBotView />;
      default:               return <DashboardView stats={stats} revenueData={revenueData} recentActivity={recentActivity} products={products} activePeriod={activePeriod} setActivePeriod={setActivePeriod} setCurrentView={setCurrentView} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} />;
    }
  };

  return (
    <div className={`admin-dashboard ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
      <AdminSidebar
        currentView={currentView}
        setCurrentView={setCurrentView}
        onLogout={onLogout}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />
      <main className="admin-main">{renderView()}</main>
    </div>
  );
}

export default AdminDashboard;




































// import React, { useState, useEffect } from 'react';
// import './AdminDashboard.css';
// import JobsView from './sections/jobs/JobsView';

// import apiService from '../../services/api';
// import AdminSidebar from './components/AdminSidebar/AdminSidebar';

// import SuperAdminView     from './sections/SuperAdminView/SuperAdminView';
// import DashboardView      from './sections/DashboardView/DashboardView';
// import ProductsView       from './sections/ProductsView/ProductsView';
// import PromotionsView     from './sections/PromotionsView/PromotionsView';  
// import UsersView          from './sections/UsersView/UsersView';
// import ShopsView          from './sections/ShopsView/ShopsView';
// import CategoriesView     from './sections/CategoriesView/CategoriesView';
// import ReviewsView        from './sections/ReviewsView/ReviewsView';
// import AnalyticsView      from './sections/AnalyticsView/AnalyticsView';
// import UserCommunications from './sections/UserComms/UserCommunications';
// import ChatBotView        from './sections/ChatBotView/ChatBotView';
// import ReferredUsersPage from './sections/ReferredUsersPage/ReferredUsersPage';

// const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';
// const PRODUCTS_PER_PAGE = 100;

// function AdminDashboard({ onLogout }) {
//   const [stats, setStats]               = useState(null);
//   const [revenueData, setRevenueData]   = useState([]);
//   const [recentActivity, setRecentActivity] = useState([]);
//   const [products, setProducts]         = useState([]);
//   const [orders, setOrders]             = useState([]);   // ✅ kept for any future order views
//   const [users, setUsers]               = useState([]);
//   const [shops, setShops]               = useState([]);
//   const [categories, setCategories]     = useState([]);
//   const [activePeriod, setActivePeriod] = useState('month');
//   const [loading, setLoading]           = useState(true);
//   const [referrals, setReferrals]             = useState([]);
//   const [referralStats, setReferralStats]     = useState(null);

//   // ✅ Sidebar collapsed state
//   const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

//   const [productsPagination, setProductsPagination] = useState({
//     currentPage: 1, totalPages: 1, totalCount: 0, hasNext: false, hasPrevious: false,
//   });
//   const [productStats, setProductStats] = useState(null);
//   const [productsFilters, setProductsFilters] = useState({
//     category: '', status: '', search: '', plan_type: '',
//   });

//   const [currentView, setCurrentView] = useState(() => {
//     const saved = localStorage.getItem('adminCurrentView') || 'overview';
//     // ✅ Migrate any saved 'orders' to 'promotions' automatically
//     return saved === 'orders' ? 'promotions' : saved;
//   });

//   useEffect(() => {
//     if (currentView) localStorage.setItem('adminCurrentView', currentView);
//   }, [currentView]);


//   // ── Admin guard — runs on mount + focus + tab visibility ─────────────────────
//   // Boots the user out if:
//   //   1. No token
//   //   2. /auth/profile/ returns 401/403 (suspended, invalid token, not in UserProfile)
//   //   3. /auth/profile/ returns 404 (email not in UserProfile table)
//   //   4. is_active === false (account explicitly suspended)
//   //   5. is_staff and is_superuser are BOTH explicitly false (confirmed non-admin)
//   //
//   // ⚠️  NOTE: if /auth/profile/ does NOT return is_staff/is_superuser in its
//   //  response body (they live on auth.User, not UserProfile), we skip the role
//   //  check entirely — the backend's own 403 on admin API calls is the safety net.
//   //  Check your browser console for the 🔍 log line to see what fields arrive.
//   useEffect(() => {
//     const guardAdmin = async () => {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token')) || localStorage.getItem('authToken') || '';
//       if (!token) {
//         console.warn('🔒 AdminDashboard: no token — redirecting to home.');
//         onLogout?.();
//         return;
//       }
//       try {
//         const res = await fetch(`${API_BASE_URL}/auth/profile/`, {
//           headers: { Authorization: `Token ${token}` },
//         });

//         // 401 / 403 → token invalid, suspended, or not in UserProfile
//         if (res.status === 401 || res.status === 403) {
//           console.warn(`🔒 AdminDashboard: profile returned ${res.status} — forcing logout.`);
//           onLogout?.();
//           return;
//         }

//         // 404 → email not found in UserProfile table
//         if (res.status === 404) {
//           console.warn('🔒 AdminDashboard: user not found in UserProfile — forcing logout.');
//           onLogout?.();
//           return;
//         }

//         if (!res.ok) {
//           console.warn(`🔒 AdminDashboard: unexpected profile status ${res.status} — forcing logout.`);
//           onLogout?.();
//           return;
//         }

//         const profile = await res.json();

//         // Log what the profile endpoint actually returns so we can debug field names
//         console.log('🔍 AdminDashboard guard profile fields:', JSON.stringify({
//           is_staff:     profile.is_staff,
//           is_superuser: profile.is_superuser,
//           is_active:    profile.is_active,
//           email:        profile.email || profile.identifier,
//         }));

//         // Suspended — only kick if explicitly false (not undefined/null)
//         if (profile.is_active === false) {
//           console.warn('🔒 AdminDashboard: account suspended — forcing logout.');
//           onLogout?.();
//           return;
//         }

//         // Role check — ONLY run if the profile endpoint actually returns these fields.
//         // If both are undefined the endpoint doesn't expose them; skip and let
//         // the backend's own 403 on admin endpoints be the safety net.
//         const fieldsPresent = profile.is_staff !== undefined || profile.is_superuser !== undefined;
//         if (fieldsPresent && profile.is_staff !== true && profile.is_superuser !== true) {
//           console.warn('🔒 AdminDashboard: user is not staff/superuser — redirecting to home.');
//           onLogout?.();
//           return;
//         }

//         console.log(`✅ AdminDashboard: guard passed for ${profile.email || profile.identifier}`);
//       } catch (err) {
//         // Network error — don't kick the user out for a flaky connection
//         console.warn('AdminDashboard guard: network error (non-fatal):', err.message);
//       }
//     };

//     guardAdmin();
//     window.addEventListener('focus', guardAdmin);
//     const onVisibility = () => { if (document.visibilityState === 'visible') guardAdmin(); };
//     document.addEventListener('visibilitychange', onVisibility);
//     return () => {
//       window.removeEventListener('focus', guardAdmin);
//       document.removeEventListener('visibilitychange', onVisibility);
//     };
//   }, [onLogout]);

//   useEffect(() => { setTimeout(fetchDashboardData, 3500); }, []);

//   useEffect(() => {
//     if (currentView === 'overview' || currentView === 'admin') fetchRevenueData(activePeriod);
//   }, [activePeriod, currentView]);

//   useEffect(() => {
//     if (currentView === 'products') fetchProducts(productsPagination.currentPage);
//   }, [productsPagination.currentPage, productsFilters]);

//   const fetchDashboardData = async () => {
//     try {
//       setLoading(true);
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       if (!token) { alert('Authentication required.'); onLogout?.(); return; }

//       const statsRes = await fetch(`${API_BASE_URL}/admin/dashboard/stats/`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (statsRes.ok) {
//         setStats(await statsRes.json());
//       } else if (statsRes.status === 401 || statsRes.status === 403) {
//         alert('Session expired. Please log in again.'); onLogout?.(); return;
//       }

//       const actRes = await fetch(`${API_BASE_URL}/admin/dashboard/activity/`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (actRes.ok) setRecentActivity(await actRes.json());

//       await fetchProducts(1);
//       setOrders(generateMockOrders());
//       setUsers(generateMockUsers());
//       setShops(generateMockShops());
//       setCategories(generateMockCategories());
//       setLoading(false);
//     } catch (e) {
//       console.error('❌ Dashboard error:', e);
//       setLoading(false);
//       alert('Failed to load dashboard data.');
//     }
//   };

//   const fetchProducts = async (page = 1) => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const params = new URLSearchParams({ page, page_size: PRODUCTS_PER_PAGE });
//       if (productsFilters.category)  params.append('category',  productsFilters.category);
//       if (productsFilters.status)    params.append('status',    productsFilters.status);
//       if (productsFilters.search)    params.append('search',    productsFilters.search);
//       if (productsFilters.plan_type) params.append('plan_type', productsFilters.plan_type);

//       const res = await fetch(`${API_BASE_URL}/admin/products/?${params}`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (!res.ok) return;

//       const data        = await res.json();
//       const rawProducts = data.results || (Array.isArray(data) ? data : []);
//       const serverTotal = data.count  || rawProducts.length;
//       const transformed = rawProducts.map(p => apiService.transformAdminProductData(p));

//       setProducts(transformed);
//       setProductsPagination({
//         currentPage: page, totalPages: Math.ceil(serverTotal / PRODUCTS_PER_PAGE),
//         totalCount: serverTotal, hasNext: !!data.next, hasPrevious: !!data.previous,
//       });

//       const ago7 = new Date(); ago7.setDate(ago7.getDate() - 7);
//       setProductStats({
//         total:    serverTotal,
//         active:   transformed.filter(p => p.status === 'Active' || p.is_active).length,
//         inactive: transformed.filter(p => p.status !== 'Active' && !p.is_active).length,
//         newLast7: transformed.filter(p => { const d = new Date(p.date_added || p.created_at); return !isNaN(d) && d >= ago7; }).length,
//       });
//         if (rawProducts.length > 0 && page === 1) {
//           const sample = rawProducts[0];
////         }
//     } catch (e) { console.error('❌ fetchProducts:', e); }
//   };

//   const fetchRevenueData = async (period) => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const res   = await fetch(`${API_BASE_URL}/admin/dashboard/revenue/?period=${period}`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (res.ok) { const d = await res.json(); setRevenueData(d.data || []); }
//     } catch (e) { console.error('Revenue error:', e); }
//   };

//   const fetchReferralData = async () => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const res = await fetch(`${API_BASE_URL}/admin/referrals/`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (res.ok) {
//         const data = await res.json();
//         setReferrals(data.results || data.referrals || []);
//         setReferralStats(data.stats || { total: data.length, active: 0, earnings: 0 });
//       }
//     } catch (e) {
//       console.error('❌ Fetch referrals error:', e);
//     }
//   };

//   const handleDeleteProduct = async (productId) => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const res   = await fetch(`${API_BASE_URL}/admin/products/${productId}/`, {
//         method: 'DELETE',
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (res.ok) fetchProducts(productsPagination.currentPage);
//     } catch (e) { console.error('Delete error:', e); }
//   };

//   const handleNextPage     = () => { if (productsPagination.hasNext)     setProductsPagination(p => ({ ...p, currentPage: p.currentPage + 1 })); };
//   const handlePreviousPage = () => { if (productsPagination.hasPrevious) setProductsPagination(p => ({ ...p, currentPage: p.currentPage - 1 })); };
//   const handleGoToPage     = (page) => { if (page >= 1 && page <= productsPagination.totalPages) setProductsPagination(p => ({ ...p, currentPage: page })); };

//   const generateMockOrders = () => [
//     { id: 1, orderId: '#ORD-2024-001', customer: 'John Doe',       product: 'iPhone 13 Pro',        amount: 125000, status: 'Completed',  date: '2024-02-10' },
//     { id: 2, orderId: '#ORD-2024-002', customer: 'Jane Smith',     product: 'MacBook Air M2',       amount: 145000, status: 'Processing', date: '2024-02-11' },
//     { id: 3, orderId: '#ORD-2024-003', customer: 'Mike Johnson',   product: 'AirPods Pro',          amount:  35000, status: 'Pending',    date: '2024-02-12' },
//     { id: 4, orderId: '#ORD-2024-004', customer: 'Sarah Williams', product: 'iPad Pro 12.9"',       amount:  98000, status: 'Completed',  date: '2024-02-12' },
//     { id: 5, orderId: '#ORD-2024-005', customer: 'David Brown',    product: 'Apple Watch Series 9', amount:  52000, status: 'Cancelled',  date: '2024-02-13' },
//   ];

//   const generateMockUsers = () => [
//     { id: 1, name: 'Alice Johnson',  email: 'alice@example.com',  phone: '+254 712 345 678', status: 'Active',    joined: '2023-08-15', listings: 12 },
//     { id: 2, name: 'Bob Wilson',     email: 'bob@example.com',    phone: '+254 723 456 789', status: 'Active',    joined: '2023-09-22', listings:  8 },
//     { id: 3, name: 'Carol Martinez', email: 'carol@example.com',  phone: '+254 734 567 890', status: 'Suspended', joined: '2023-10-10', listings:  3 },
//     { id: 4, name: 'Daniel Lee',     email: 'daniel@example.com', phone: '+254 745 678 901', status: 'Active',    joined: '2023-11-05', listings: 15 },
//     { id: 5, name: 'Emma Davis',     email: 'emma@example.com',   phone: '+254 756 789 012', status: 'Active',    joined: '2024-01-12', listings:  6 },
//   ];
//   const generateMockShops = () => [
//     { id: 1, name: 'Tech Haven',      owner: 'John Doe',       category: 'Electronics',   status: 'Approved', products: 45, rating: 4.8, joined: '2023-06-15' },
//     { id: 2, name: 'Fashion Forward', owner: 'Jane Smith',     category: 'Fashion',       status: 'Pending',  products: 32, rating: 4.5, joined: '2024-01-20' },
//     { id: 3, name: 'Home Essentials', owner: 'Mike Johnson',   category: 'Home & Garden', status: 'Approved', products: 67, rating: 4.9, joined: '2023-09-10' },
//     { id: 4, name: 'Auto Parts Pro',  owner: 'Sarah Williams', category: 'Vehicles',      status: 'Approved', products: 28, rating: 4.6, joined: '2023-11-05' },
//     { id: 5, name: 'Book Corner',     owner: 'David Brown',    category: 'Books',         status: 'Rejected', products:  0, rating:   0, joined: '2024-02-01' },
//   ];
//   const generateMockCategories = () => [
//     { id: 1, name: 'Electronics',   icon: '📱', productCount: 245, status: 'Active', subCategories: 12 },
//     { id: 2, name: 'Vehicles',      icon: '🚗', productCount: 189, status: 'Active', subCategories:  8 },
//     { id: 3, name: 'Property',      icon: '🏠', productCount: 156, status: 'Active', subCategories:  6 },
//     { id: 4, name: 'Fashion',       icon: '👗', productCount: 312, status: 'Active', subCategories: 15 },
//     { id: 5, name: 'Home & Garden', icon: '🛋️', productCount: 203, status: 'Active', subCategories: 10 },
//     { id: 6, name: 'Sports',        icon: '⚽', productCount:  98, status: 'Active', subCategories:  7 },
//   ];

//   if (loading) {
//     return (
//       <div className="admin-dashboard">
//         <div className="admin-loading"><div className="spinner"></div><p>Loading dashboard...</p></div>
//       </div>
//     );
//   }

//   const renderView = () => {
//     switch (currentView) {
//       case 'admin':    return <SuperAdminView stats={stats} revenueData={revenueData} recentActivity={recentActivity} activePeriod={activePeriod} setActivePeriod={setActivePeriod} onLogout={onLogout} />;
//       case 'overview': return <DashboardView stats={stats} revenueData={revenueData} recentActivity={recentActivity} products={products} activePeriod={activePeriod} setActivePeriod={setActivePeriod} setCurrentView={setCurrentView} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} />;
//       case 'products': return <ProductsView products={products} productStats={productStats} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} pagination={productsPagination} onNextPage={handleNextPage} onPreviousPage={handlePreviousPage} onGoToPage={handleGoToPage} filters={productsFilters} onFilterChange={setProductsFilters} />;

//       case 'promotions':     return <PromotionsView onLogout={onLogout} />;   // ✅ was 'orders'

//       case 'users':          return <UsersView users={users} onLogout={onLogout} />;
//       case 'referrals':      return <ReferredUsersPage referrals={referrals} stats={referralStats} onLogout={onLogout} />;
//       case 'shops':          return <ShopsView shops={shops} onLogout={onLogout} />;
//       case 'categories':     return <CategoriesView categories={categories} onLogout={onLogout} />;
//       case 'jobs':           return <JobsView onLogout={onLogout} />;
//       case 'reviews':        return <ReviewsView onLogout={onLogout} />;
//       case 'analytics':      return <AnalyticsView onLogout={onLogout} />;
//       case 'communications': return <UserCommunications />;
//       case 'chatbot':        return <ChatBotView />;
//       default:               return <DashboardView stats={stats} revenueData={revenueData} recentActivity={recentActivity} products={products} activePeriod={activePeriod} setActivePeriod={setActivePeriod} setCurrentView={setCurrentView} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} />;
//     }
//   };

//   return (
//     <div className={`admin-dashboard ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
//       <AdminSidebar
//         currentView={currentView}
//         setCurrentView={setCurrentView}
//         onLogout={onLogout}
//         collapsed={sidebarCollapsed}
//         setCollapsed={setSidebarCollapsed}
//       />
//       <main className="admin-main">{renderView()}</main>
//     </div>
//   );
// }

// export default AdminDashboard;
































































// import React, { useState, useEffect } from 'react';
// import './AdminDashboard.css';
// import JobsView from './sections/jobs/JobsView';

// import apiService from '../../services/api';
// import AdminSidebar from './components/AdminSidebar/AdminSidebar';

// import SuperAdminView     from './sections/SuperAdminView/SuperAdminView';
// import DashboardView      from './sections/DashboardView/DashboardView';
// import ProductsView       from './sections/ProductsView/ProductsView';
// import PromotionsView     from './sections/PromotionsView/PromotionsView';  
// import UsersView          from './sections/UsersView/UsersView';
// import ShopsView          from './sections/ShopsView/ShopsView';
// import CategoriesView     from './sections/CategoriesView/CategoriesView';
// import ReviewsView        from './sections/ReviewsView/ReviewsView';
// import AnalyticsView      from './sections/AnalyticsView/AnalyticsView';
// import UserCommunications from './sections/UserComms/UserCommunications';
// import ChatBotView        from './sections/ChatBotView/ChatBotView';
// import ReferredUsersPage from './sections/ReferredUsersPage/ReferredUsersPage';

// const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';
// const PRODUCTS_PER_PAGE = 100;

// function AdminDashboard({ onLogout }) {
//   const [stats, setStats]               = useState(null);
//   const [revenueData, setRevenueData]   = useState([]);
//   const [recentActivity, setRecentActivity] = useState([]);
//   const [products, setProducts]         = useState([]);
//   const [orders, setOrders]             = useState([]);   // ✅ kept for any future order views
//   const [users, setUsers]               = useState([]);
//   const [shops, setShops]               = useState([]);
//   const [categories, setCategories]     = useState([]);
//   const [activePeriod, setActivePeriod] = useState('month');
//   const [loading, setLoading]           = useState(true);
//   const [referrals, setReferrals]             = useState([]);
//   const [referralStats, setReferralStats]     = useState(null);

//   // ✅ Sidebar collapsed state
//   const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

//   const [productsPagination, setProductsPagination] = useState({
//     currentPage: 1, totalPages: 1, totalCount: 0, hasNext: false, hasPrevious: false,
//   });
//   const [productStats, setProductStats] = useState(null);
//   const [productsFilters, setProductsFilters] = useState({
//     category: '', status: '', search: '', plan_type: '',
//   });

//   const [currentView, setCurrentView] = useState(() => {
//     const saved = localStorage.getItem('adminCurrentView') || 'overview';
//     // ✅ Migrate any saved 'orders' to 'promotions' automatically
//     return saved === 'orders' ? 'promotions' : saved;
//   });

//   useEffect(() => {
//     if (currentView) localStorage.setItem('adminCurrentView', currentView);
//   }, [currentView]);

//   useEffect(() => { fetchDashboardData(); }, []);

//   useEffect(() => {
//     if (currentView === 'overview' || currentView === 'admin') fetchRevenueData(activePeriod);
//   }, [activePeriod, currentView]);

//   useEffect(() => {
//     if (currentView === 'products') fetchProducts(productsPagination.currentPage);
//   }, [productsPagination.currentPage, productsFilters]);

//   const fetchDashboardData = async () => {
//     try {
//       setLoading(true);
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       if (!token) { alert('Authentication required.'); onLogout?.(); return; }

//       const statsRes = await fetch(`${API_BASE_URL}/admin/dashboard/stats/`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (statsRes.ok) {
//         setStats(await statsRes.json());
//       } else if (statsRes.status === 401 || statsRes.status === 403) {
//         alert('Session expired. Please log in again.'); onLogout?.(); return;
//       }

//       const actRes = await fetch(`${API_BASE_URL}/admin/dashboard/activity/`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (actRes.ok) setRecentActivity(await actRes.json());

//       await fetchProducts(1);
//       setOrders(generateMockOrders());
//       setUsers(generateMockUsers());
//       setShops(generateMockShops());
//       setCategories(generateMockCategories());
//       setLoading(false);
//     } catch (e) {
//       console.error('❌ Dashboard error:', e);
//       setLoading(false);
//       alert('Failed to load dashboard data.');
//     }
//   };

//   const fetchProducts = async (page = 1) => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const params = new URLSearchParams({ page, page_size: PRODUCTS_PER_PAGE });
//       if (productsFilters.category)  params.append('category',  productsFilters.category);
//       if (productsFilters.status)    params.append('status',    productsFilters.status);
//       if (productsFilters.search)    params.append('search',    productsFilters.search);
//       if (productsFilters.plan_type) params.append('plan_type', productsFilters.plan_type);

//       const res = await fetch(`${API_BASE_URL}/admin/products/?${params}`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (!res.ok) return;

//       const data        = await res.json();
//       const rawProducts = data.results || (Array.isArray(data) ? data : []);
//       const serverTotal = data.count  || rawProducts.length;
//       const transformed = rawProducts.map(p => apiService.transformAdminProductData(p));

//       setProducts(transformed);
//       setProductsPagination({
//         currentPage: page, totalPages: Math.ceil(serverTotal / PRODUCTS_PER_PAGE),
//         totalCount: serverTotal, hasNext: !!data.next, hasPrevious: !!data.previous,
//       });

//       const ago7 = new Date(); ago7.setDate(ago7.getDate() - 7);
//       setProductStats({
//         total:    serverTotal,
//         active:   transformed.filter(p => p.status === 'Active' || p.is_active).length,
//         inactive: transformed.filter(p => p.status !== 'Active' && !p.is_active).length,
//         newLast7: transformed.filter(p => { const d = new Date(p.date_added || p.created_at); return !isNaN(d) && d >= ago7; }).length,
//       });
//         if (rawProducts.length > 0 && page === 1) {
//           const sample = rawProducts[0];
////         }
//     } catch (e) { console.error('❌ fetchProducts:', e); }
//   };

//   const fetchRevenueData = async (period) => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const res   = await fetch(`${API_BASE_URL}/admin/dashboard/revenue/?period=${period}`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (res.ok) { const d = await res.json(); setRevenueData(d.data || []); }
//     } catch (e) { console.error('Revenue error:', e); }
//   };

//   const fetchReferralData = async () => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const res = await fetch(`${API_BASE_URL}/admin/referrals/`, {
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (res.ok) {
//         const data = await res.json();
//         setReferrals(data.results || data.referrals || []);
//         setReferralStats(data.stats || { total: data.length, active: 0, earnings: 0 });
//       }
//     } catch (e) {
//       console.error('❌ Fetch referrals error:', e);
//     }
//   };

//   const handleDeleteProduct = async (productId) => {
//     try {
//       const token = (localStorage.getItem('authToken') || localStorage.getItem('token'));
//       const res   = await fetch(`${API_BASE_URL}/admin/products/${productId}/`, {
//         method: 'DELETE',
//         headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
//       });
//       if (res.ok) fetchProducts(productsPagination.currentPage);
//     } catch (e) { console.error('Delete error:', e); }
//   };

//   const handleNextPage     = () => { if (productsPagination.hasNext)     setProductsPagination(p => ({ ...p, currentPage: p.currentPage + 1 })); };
//   const handlePreviousPage = () => { if (productsPagination.hasPrevious) setProductsPagination(p => ({ ...p, currentPage: p.currentPage - 1 })); };
//   const handleGoToPage     = (page) => { if (page >= 1 && page <= productsPagination.totalPages) setProductsPagination(p => ({ ...p, currentPage: page })); };

//   const generateMockOrders = () => [
//     { id: 1, orderId: '#ORD-2024-001', customer: 'John Doe',       product: 'iPhone 13 Pro',        amount: 125000, status: 'Completed',  date: '2024-02-10' },
//     { id: 2, orderId: '#ORD-2024-002', customer: 'Jane Smith',     product: 'MacBook Air M2',       amount: 145000, status: 'Processing', date: '2024-02-11' },
//     { id: 3, orderId: '#ORD-2024-003', customer: 'Mike Johnson',   product: 'AirPods Pro',          amount:  35000, status: 'Pending',    date: '2024-02-12' },
//     { id: 4, orderId: '#ORD-2024-004', customer: 'Sarah Williams', product: 'iPad Pro 12.9"',       amount:  98000, status: 'Completed',  date: '2024-02-12' },
//     { id: 5, orderId: '#ORD-2024-005', customer: 'David Brown',    product: 'Apple Watch Series 9', amount:  52000, status: 'Cancelled',  date: '2024-02-13' },
//   ];

//   const generateMockUsers = () => [
//     { id: 1, name: 'Alice Johnson',  email: 'alice@example.com',  phone: '+254 712 345 678', status: 'Active',    joined: '2023-08-15', listings: 12 },
//     { id: 2, name: 'Bob Wilson',     email: 'bob@example.com',    phone: '+254 723 456 789', status: 'Active',    joined: '2023-09-22', listings:  8 },
//     { id: 3, name: 'Carol Martinez', email: 'carol@example.com',  phone: '+254 734 567 890', status: 'Suspended', joined: '2023-10-10', listings:  3 },
//     { id: 4, name: 'Daniel Lee',     email: 'daniel@example.com', phone: '+254 745 678 901', status: 'Active',    joined: '2023-11-05', listings: 15 },
//     { id: 5, name: 'Emma Davis',     email: 'emma@example.com',   phone: '+254 756 789 012', status: 'Active',    joined: '2024-01-12', listings:  6 },
//   ];
//   const generateMockShops = () => [
//     { id: 1, name: 'Tech Haven',      owner: 'John Doe',       category: 'Electronics',   status: 'Approved', products: 45, rating: 4.8, joined: '2023-06-15' },
//     { id: 2, name: 'Fashion Forward', owner: 'Jane Smith',     category: 'Fashion',       status: 'Pending',  products: 32, rating: 4.5, joined: '2024-01-20' },
//     { id: 3, name: 'Home Essentials', owner: 'Mike Johnson',   category: 'Home & Garden', status: 'Approved', products: 67, rating: 4.9, joined: '2023-09-10' },
//     { id: 4, name: 'Auto Parts Pro',  owner: 'Sarah Williams', category: 'Vehicles',      status: 'Approved', products: 28, rating: 4.6, joined: '2023-11-05' },
//     { id: 5, name: 'Book Corner',     owner: 'David Brown',    category: 'Books',         status: 'Rejected', products:  0, rating:   0, joined: '2024-02-01' },
//   ];
//   const generateMockCategories = () => [
//     { id: 1, name: 'Electronics',   icon: '📱', productCount: 245, status: 'Active', subCategories: 12 },
//     { id: 2, name: 'Vehicles',      icon: '🚗', productCount: 189, status: 'Active', subCategories:  8 },
//     { id: 3, name: 'Property',      icon: '🏠', productCount: 156, status: 'Active', subCategories:  6 },
//     { id: 4, name: 'Fashion',       icon: '👗', productCount: 312, status: 'Active', subCategories: 15 },
//     { id: 5, name: 'Home & Garden', icon: '🛋️', productCount: 203, status: 'Active', subCategories: 10 },
//     { id: 6, name: 'Sports',        icon: '⚽', productCount:  98, status: 'Active', subCategories:  7 },
//   ];

//   if (loading) {
//     return (
//       <div className="admin-dashboard">
//         <div className="admin-loading"><div className="spinner"></div><p>Loading dashboard...</p></div>
//       </div>
//     );
//   }

//   const renderView = () => {
//     switch (currentView) {
//       case 'admin':    return <SuperAdminView stats={stats} revenueData={revenueData} recentActivity={recentActivity} activePeriod={activePeriod} setActivePeriod={setActivePeriod} onLogout={onLogout} />;
//       case 'overview': return <DashboardView stats={stats} revenueData={revenueData} recentActivity={recentActivity} products={products} activePeriod={activePeriod} setActivePeriod={setActivePeriod} setCurrentView={setCurrentView} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} />;
//       case 'products': return <ProductsView products={products} productStats={productStats} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} pagination={productsPagination} onNextPage={handleNextPage} onPreviousPage={handlePreviousPage} onGoToPage={handleGoToPage} filters={productsFilters} onFilterChange={setProductsFilters} />;

//       case 'promotions':     return <PromotionsView onLogout={onLogout} />;   // ✅ was 'orders'

//       case 'users':          return <UsersView users={users} onLogout={onLogout} />;
//       case 'referrals':      return <ReferredUsersPage referrals={referrals} stats={referralStats} onLogout={onLogout} />;
//       case 'shops':          return <ShopsView shops={shops} onLogout={onLogout} />;
//       case 'categories':     return <CategoriesView categories={categories} onLogout={onLogout} />;
//       case 'jobs':           return <JobsView onLogout={onLogout} />;
//       case 'reviews':        return <ReviewsView onLogout={onLogout} />;
//       case 'analytics':      return <AnalyticsView onLogout={onLogout} />;
//       case 'communications': return <UserCommunications />;
//       case 'chatbot':        return <ChatBotView />;
//       default:               return <DashboardView stats={stats} revenueData={revenueData} recentActivity={recentActivity} products={products} activePeriod={activePeriod} setActivePeriod={setActivePeriod} setCurrentView={setCurrentView} handleDeleteProduct={handleDeleteProduct} onLogout={onLogout} />;
//     }
//   };

//   return (
//     <div className={`admin-dashboard ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
//       <AdminSidebar
//         currentView={currentView}
//         setCurrentView={setCurrentView}
//         onLogout={onLogout}
//         collapsed={sidebarCollapsed}
//         setCollapsed={setSidebarCollapsed}
//       />
//       <main className="admin-main">{renderView()}</main>
//     </div>
//   );
// }

// export default AdminDashboard;





















